import { PaletteOptions, ThemeOptions } from '@mui/material'
import { defaultTheme } from 'react-admin'
import { PortalBranding } from '../contexts/BrandingContext'
import { border } from './theme/border'
import { color, themeColor, xxllncColor } from './theme/color'
import { muiOverrides } from './theme/muiOverrides'
import { raOverrides } from './theme/raOverrides'
import { typography } from './theme/typography'

export * from './theme/border'
export * from './theme/button'
export * from './theme/color'
export * from './theme/typography'

const SAFE_FONT_FAMILY = /^[a-zA-Z0-9 \-]+$/
const isSafeFontFamily = (value: string | null | undefined): boolean =>
  !!value && SAFE_FONT_FAMILY.test(value)

interface ITheme extends ThemeOptions {
  palette?: PaletteOptions & {
    common: {
      [key: string]: string;
    }
  }
  sidebar: {
    width: number;
    closedWidth: number;
  }
}

export const buildTheme = (branding?: PortalBranding | null): ITheme => {
  const isBranded = !!(branding?.primary_color || branding?.admin_title)
  const headerBg = branding?.header_color || color.white
  const primaryMain = branding?.primary_color || themeColor.blue
  const fontFamily = isSafeFontFamily(branding?.font_family)
    ? `'${branding!.font_family}', Helvetica, Arial, sans-serif`
    : typography.body
  const bgDefault = branding?.background_color || themeColor.slate05
  const borderRadius = branding?.border_radius ?? border.radius.md

  const brandedOverrides = isBranded ? {
    RaSaveButton: {
      styleOverrides: {
        root: {
          ...raOverrides.RaSaveButton?.styleOverrides?.root,
          ['&.MuiButton-contained']: {
            backgroundColor: primaryMain,
            color: '#fff',
            ['&:hover']: {
              backgroundColor: primaryMain,
              filter: 'brightness(0.85)',
            },
          },
          ['&.Mui-disabled']: {
            backgroundColor: primaryMain,
            opacity: 0.4,
            color: '#fff',
          },
        },
      },
    },
  } : {}

  return {
    ...defaultTheme,
    typography: {
      fontFamily,
      h6: {
        color: themeColor.black,
        fontSize: 18,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '24px',
        letterSpacing: '-0.01em',
      },
      subtitle1: {
        color: themeColor.slate80,
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px',
      },
    },
    palette: {
      common: {
        black: themeColor.black,
        white: '#fff',
      },
      primary: {
        main: primaryMain,
        contrastText: '#fff',
      },
      secondary: {
        light: themeColor.slate40,
        main: themeColor.slate,
        dark: themeColor.black,
        contrastText: '#fff',
      },
      background: {
        default: bgDefault,
      },
      text: {
        primary: themeColor.black,
      },
      error: {
        main: xxllncColor.red100,
      },
      mode: 'light' as unknown as 'light',
    },
    sidebar: {
      width: 240,
      closedWidth: 64,
    },
    shape: {
      borderRadius,
    },
    components: {
      ...defaultTheme.components,
      ...raOverrides,
      ...muiOverrides,
      MuiAppBar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
          colorSecondary: {
            backgroundColor: headerBg,
            border: 0,
            borderBottom: border.thin + ' solid ' + themeColor.slate10,
          },
        },
      },
      ...brandedOverrides,
    },
  }
}

export const theme: ITheme = buildTheme()
